import React, { useEffect, useState, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { getJob, getDocuments, downloadDocument } from '../../services/api';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { useLanguage } from '../../context/LanguageContext';
import {
  ArrowLeft,
  Calendar,
  User,
  FileText,
  Clock,
  CheckCircle,
  AlertCircle,
  Download
} from 'lucide-react';

const JobDetail = () => {
  const { t, locale } = useLanguage();
  const statusLabels = {
    planned: t('portalUi.status.planned', 'Plānots'),
    in_progress: t('portalUi.status.inProgress', 'Procesā'),
    completed: t('portalUi.status.completed', 'Pabeigts')
  };

  const statusColors = {
    planned: 'bg-blue-100 text-blue-700 border-blue-200',
    in_progress: 'bg-amber-100 text-amber-700 border-amber-200',
    completed: 'bg-green-100 text-green-700 border-green-200'
  };

  const { jobId } = useParams();
  const [job, setJob] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const [jobData, docsData] = await Promise.all([
        getJob(jobId),
        getDocuments({ jobId })
      ]);
      setJob(jobData);
      setDocuments(docsData);
    } catch (error) {
      console.error('Failed to fetch job:', error);
    } finally {
      setLoading(false);
    }
  }, [jobId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleDownload = async (doc) => {
    try {
      await downloadDocument(doc.id, doc.original_filename);
    } catch (error) {
      console.error('Failed to download document:', error);
      toast.error(t('portalUi.client.jobDetail.downloadError', 'Neizdevās lejupielādēt dokumentu'));
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-[#1F3D2B] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!job) {
    return (
      <div className="text-center py-12" data-testid="job-detail-not-found">
        <AlertCircle className="w-16 h-16 mx-auto mb-4 text-red-500" />
        <h2 className="text-xl font-semibold text-[#2E2E2E] mb-2">{t('portalUi.client.jobDetail.notFoundTitle', 'Darbs nav atrasts')}</h2>
        <Link to="/portal/jobs">
          <Button variant="outline">{t('portalUi.client.jobDetail.backToJobs', 'Atpakaļ pie darbiem')}</Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Back button */}
      <Link to="/portal/jobs" className="inline-flex items-center gap-2 text-[#2E2E2E]/60 hover:text-[#1F3D2B] transition-colors" data-testid="job-detail-back">
        <ArrowLeft className="w-4 h-4" />
        {t('portalUi.client.jobDetail.backToJobs', 'Atpakaļ pie darbiem')}
      </Link>

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-[#2E2E2E]">{job.title}</h1>
          {job.description && (
            <p className="text-[#2E2E2E]/60 mt-1">{job.description}</p>
          )}
        </div>
        <span className={`px-4 py-2 rounded-full text-sm font-medium border self-start ${statusColors[job.status]}`}>
          {statusLabels[job.status]}
        </span>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Main info */}
        <div className="lg:col-span-2 space-y-6">
          {/* Details */}
          <Card className="border-0 shadow-sm">
            <CardHeader>
              <CardTitle className="text-lg">{t('portalUi.client.jobDetail.infoTitle', 'Darba informācija')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {job.responsible_person && (
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-[#1F3D2B]/10 flex items-center justify-center">
                    <User className="w-5 h-5 text-[#1F3D2B]" />
                  </div>
                  <div>
                    <p className="text-sm text-[#2E2E2E]/60">{t('portalUi.client.jobDetail.responsibleLabel', 'Atbildīgais')}</p>
                    <p className="font-medium text-[#2E2E2E]">{job.responsible_person}</p>
                  </div>
                </div>
              )}
              {job.start_date && (
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-[#1F3D2B]/10 flex items-center justify-center">
                    <Calendar className="w-5 h-5 text-[#1F3D2B]" />
                  </div>
                  <div>
                    <p className="text-sm text-[#2E2E2E]/60">{t('portalUi.client.jobDetail.startDateLabel', 'Sākuma datums')}</p>
                    <p className="font-medium text-[#2E2E2E]">
                      {new Date(job.start_date).toLocaleDateString(locale)}
                    </p>
                  </div>
                </div>
              )}
              {job.planned_end_date && (
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-[#1F3D2B]/10 flex items-center justify-center">
                    <Clock className="w-5 h-5 text-[#1F3D2B]" />
                  </div>
                  <div>
                    <p className="text-sm text-[#2E2E2E]/60">{t('portalUi.client.jobDetail.plannedEndLabel', 'Plānotais beigu datums')}</p>
                    <p className="font-medium text-[#2E2E2E]">
                      {new Date(job.planned_end_date).toLocaleDateString(locale)}
                    </p>
                  </div>
                </div>
              )}
              {job.completed_date && (
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                    <CheckCircle className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <p className="text-sm text-[#2E2E2E]/60">{t('portalUi.client.jobDetail.completedLabel', 'Pabeigts')}</p>
                    <p className="font-medium text-[#2E2E2E]">
                      {new Date(job.completed_date).toLocaleDateString(locale)}
                    </p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Notes */}
          {job.notes && (
            <Card className="border-0 shadow-sm border-l-4 border-l-[#1F3D2B]">
              <CardHeader>
                <CardTitle className="text-lg">{t('portalUi.client.jobDetail.notesTitle', 'Piezīmes')}</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-[#2E2E2E]/80 whitespace-pre-wrap">{job.notes}</p>
              </CardContent>
            </Card>
          )}

          {/* Documents */}
          <Card className="border-0 shadow-sm">
            <CardHeader>
              <CardTitle className="text-lg">{t('portalUi.client.jobDetail.documentsTitle', 'Pievienotie dokumenti')}</CardTitle>
            </CardHeader>
            <CardContent>
              {documents.length > 0 ? (
                <div className="space-y-2">
                  {documents.map((doc) => (
                    <div
                      key={doc.id}
                      className="flex items-center justify-between p-3 rounded-lg bg-[#f8faf8] hover:bg-[#1F3D2B]/5 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <FileText className="w-5 h-5 text-[#1F3D2B]" />
                        <div>
                          <p className="font-medium text-[#2E2E2E]">{doc.original_filename}</p>
                          <p className="text-xs text-[#2E2E2E]/60">
                            {doc.file_type} • {(doc.file_size / 1024).toFixed(1)} KB
                          </p>
                        </div>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-[#1F3D2B]"
                        onClick={() => handleDownload(doc)}
                        data-testid={`job-document-download-${doc.id}`}
                      >
                        <Download className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-[#2E2E2E]/60 text-center py-4">{t('portalUi.client.jobDetail.documentsEmpty', 'Nav pievienotu dokumentu')}</p>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Timeline */}
        <div>
          <Card className="border-0 shadow-sm">
            <CardHeader>
              <CardTitle className="text-lg">{t('portalUi.client.jobDetail.timelineTitle', 'Statusa laika līnija')}</CardTitle>
            </CardHeader>
            <CardContent>
              {job.timeline?.length > 0 ? (
                <div className="relative">
                  <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-[#1F3D2B]/10" />
                  <div className="space-y-6">
                    {job.timeline.slice().reverse().map((entry) => (
                      <div key={`${entry.status}-${entry.date}`} className="relative flex gap-4">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center z-10 ${
                          entry.status === 'completed' ? 'bg-green-100' :
                          entry.status === 'in_progress' ? 'bg-amber-100' : 'bg-blue-100'
                        }`}>
                          {entry.status === 'completed' ? (
                            <CheckCircle className="w-4 h-4 text-green-600" />
                          ) : entry.status === 'in_progress' ? (
                            <Clock className="w-4 h-4 text-amber-600" />
                          ) : (
                            <Calendar className="w-4 h-4 text-blue-600" />
                          )}
                        </div>
                        <div className="flex-1 pb-6">
                          <p className="font-medium text-[#2E2E2E]">
                            {statusLabels[entry.status]}
                          </p>
                          <p className="text-sm text-[#2E2E2E]/60">
                            {new Date(entry.date).toLocaleDateString(locale, {
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </p>
                          {entry.user && (
                            <p className="text-xs text-[#2E2E2E]/40 mt-1">{entry.user}</p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <p className="text-[#2E2E2E]/60 text-center py-4">{t('portalUi.client.jobDetail.timelineEmpty', 'Nav vēstures ierakstu')}</p>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default JobDetail;
